class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onEscape = (event) => {
      if (event.code === 'Escape' || event.key === 'Escape') this.close();
    };
    this.onDrawerClick = (event) => {
      if (event.target.closest('[data-cart-drawer-close]') || event.target.id === 'CartDrawer-Overlay') this.close();
    };
    this.onHeaderClick = (event) => {
      const trigger = event.target.closest('[data-jalp-cart-trigger], #cart-icon-bubble');
      if (!trigger || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      this.open(trigger);
    };
    this.onHeaderKeydown = (event) => {
      const trigger = event.target.closest('[data-jalp-cart-trigger], #cart-icon-bubble');
      if (!trigger || !['Space', ' '].includes(event.code || event.key)) return;
      event.preventDefault();
      this.open(trigger);
    };
    this.onSectionLoad = () => this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.setAttribute('aria-hidden', 'true');
    this.addEventListener('keyup', this.onEscape);
    this.addEventListener('click', this.onDrawerClick);
    document.addEventListener('click', this.onHeaderClick);
    document.addEventListener('keydown', this.onHeaderKeydown);
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    this.removeEventListener('keyup', this.onEscape);
    this.removeEventListener('click', this.onDrawerClick);
    document.removeEventListener('click', this.onHeaderClick);
    document.removeEventListener('keydown', this.onHeaderKeydown);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    if (this.classList.contains('active')) this.close();
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('[data-jalp-cart-trigger], #cart-icon-bubble').forEach((link) => {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', 'CartDrawer');
      link.setAttribute('aria-expanded', String(this.classList.contains('active')));
    });
  }

  focusDrawer() {
    const container = this.querySelector('.drawer__inner');
    if (container && this.classList.contains('active')) trapFocus(container, container.querySelector('.drawer__close') || container);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    else if (!this.activeElement) this.setActiveElement(document.activeElement);
    this.classList.add('animate', 'active');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    this.setHeaderCartIconAccessibility();
    // Reduced-motion users do not receive transitionend.
    this.focusDrawer();
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    const wasOpen = this.classList.contains('active');
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    this.setHeaderCartIconAccessibility();
    if (wasOpen) {
      const returnTo = this.activeElement?.isConnected ? this.activeElement : document.querySelector('[data-jalp-cart-trigger], #cart-icon-bubble');
      removeTrapFocus(returnTo);
    }
  }

  renderContents(parsedState) {
    const source = this.getSectionDOM(parsedState?.sections?.['tg26-cart-drawer'], '#CartDrawer');
    const target = this.querySelector('#CartDrawer');
    if (!source?.querySelector('.drawer__inner') || !target) {
      // The add already succeeded. Never repeat it after a rendering failure.
      window.location.assign(window.routes?.cart_url || this.dataset.cartUrl || '/cart');
      return false;
    }
    this.productId = parsedState.id;
    target.innerHTML = source.innerHTML;
    target.dataset.cartCount = source.dataset.cartCount || '0';
    const count = Number.isFinite(Number(parsedState.item_count)) ? Number(parsedState.item_count) : Number(source.dataset.cartCount);
    const sourceDrawer = source.closest('cart-drawer');
    const isEmpty = Number.isFinite(count) ? count === 0 : sourceDrawer?.classList.contains('is-empty');
    this.classList.toggle('is-empty', Boolean(isEmpty));
    document.querySelectorAll('[data-jalp-cart-count]').forEach((counter) => {
      if (Number.isFinite(count)) counter.textContent = String(count);
    });
    const nativeBubble = document.getElementById('cart-icon-bubble');
    const bubble = this.getSectionDOM(parsedState.sections?.['tg26-cart-icon-bubble']);
    if (nativeBubble && !nativeBubble.matches('[data-jalp-cart-trigger]') && bubble) nativeBubble.innerHTML = bubble.innerHTML;
    this.open();
    return true;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', section: 'tg26-cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble', section: 'tg26-cart-icon-bubble' }];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) { this.activeElement = element; }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'tg26-cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'tg26-cart-icon-bubble', selector: '.shopify-section', optional: true },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
